import { FC, Dispatch, SetStateAction } from 'react';

import {
  Header,
  Button,
  SpaceBetween,
  Table,
  Flashbar,
  Box,
  TextFilter,
  Pagination,
  ButtonDropdown
} from '@awsui/components-react';
import { useCollection } from '@awsui/collection-hooks';

import { FlashbarProps } from '@awsui/components-react';
import { IUserWorkSpaceProvisionRequest } from '../../../../../interfaces/workSpaces/userWorkSpaceProvisionRequest';

import RejectUserWorkSpaceProvisionRequestForm from '../rejectUserWorkSpaceProvisionRequestForm';
import EditUserWorkSpaceProvisionRequestForm from '../editUserWorkSpaceProvisionRequestForm';
import UserWorkSpaceProvisionRequestDetails from '../userWorkSpaceProvisionRequestDetails';
import ChildModal from '../../../../common/ChildModal';

interface ViewProps {
  userWorkSpaceProvisionRequests: IUserWorkSpaceProvisionRequest[];
  selectedUserWorkSpaceProvisionRequests: IUserWorkSpaceProvisionRequest[];
  flashBarItems: FlashbarProps.MessageDefinition[];
  setFlashBarItems: Dispatch<SetStateAction<FlashbarProps.MessageDefinition[]>>;
  disableActionButtons: boolean;
  handleTableRowSelection: (user: IUserWorkSpaceProvisionRequest[]) => void;
  userWorkSpaceProvisionRequestsIsEmpty: () => boolean;
  tableIsLoading: boolean;
  setRejectUserWorkSpaceProvisionRequestModalIsVisible: Dispatch<
    SetStateAction<boolean>
  >;
  rejectUserWorkSpaceProvisionRequestModalIsVisible: boolean;
  setEditUserWorkSpaceProvisionRequestModalIsVisible: Dispatch<
    SetStateAction<boolean>
  >;
  editUserWorkSpaceProvisionRequestModalIsVisible: boolean;
  handleApproval: () => void;
  refreshUserWorkSpaceProvisionRequestTable: () => void;
  setUserWorkSpaceProvisionRequestDetailsModalIsVisible: Dispatch<
    SetStateAction<boolean>
  >;
  userWorkSpaceProvisionRequestDetailsModalIsVisible: boolean;
}

const View: FC<ViewProps> = ({
  userWorkSpaceProvisionRequests,
  selectedUserWorkSpaceProvisionRequests,
  flashBarItems,
  setFlashBarItems,
  handleTableRowSelection,
  disableActionButtons,
  userWorkSpaceProvisionRequestsIsEmpty,
  tableIsLoading,
  setRejectUserWorkSpaceProvisionRequestModalIsVisible,
  rejectUserWorkSpaceProvisionRequestModalIsVisible,
  setEditUserWorkSpaceProvisionRequestModalIsVisible,
  editUserWorkSpaceProvisionRequestModalIsVisible,
  handleApproval,
  refreshUserWorkSpaceProvisionRequestTable,
  userWorkSpaceProvisionRequestDetailsModalIsVisible,
  setUserWorkSpaceProvisionRequestDetailsModalIsVisible
}) => {
  const renderEmptyTableContent = () => {
    return (
      <Box textAlign="center" color="inherit">
        <b>No WorkSpace Provision Requests</b>
        <Box padding={{ bottom: 's' }} variant="p" color="inherit">
          No WorkSpace Provision Requests to display.
        </Box>
      </Box>
    );
  };
  const renderEmptyTableContentAfterFiltering = () => {
    return (
      <Box textAlign="center" color="inherit">
        <b>No matching WorkSpace Provision Requests</b>
      </Box>
    );
  };

  const {
    items,
    filteredItemsCount,
    collectionProps,
    filterProps,
    paginationProps
  } = useCollection(userWorkSpaceProvisionRequests, {
    filtering: {
      empty: renderEmptyTableContent(),
      noMatch: renderEmptyTableContentAfterFiltering(),
      fields: ['firstName', 'lastName', 'corpEmail', 'bundleId', 'directoryId']
    },
    pagination: { pageSize: 10 },
    sorting: {},
    selection: {}
  });
  const { selectedItems: filteredUserWorkSpaceProvisionRequests = [] } =
    collectionProps;

  const renderTableActions = () => {
    return (
      <>
        <SpaceBetween direction="horizontal" size="xs">
          <Button
            iconName="refresh"
            variant="link"
            onClick={() => refreshUserWorkSpaceProvisionRequestTable()}
          />
          <Button
            variant="primary"
            disabled={disableActionButtons}
            onClick={() =>
              setUserWorkSpaceProvisionRequestDetailsModalIsVisible(true)
            }
          >
            View
          </Button>
          <ButtonDropdown
            disabled={disableActionButtons}
            items={[
              {
                text: 'Edit',
                id: 'edit-request',
                disabled: disableActionButtons
              },
              {
                text: 'Approve',
                id: 'approve-request',
                disabled: disableActionButtons
              },
              {
                text: 'Reject',
                id: 'reject-request',
                disabled: disableActionButtons
              }
            ]}
            onItemClick={({ detail }) => {
              if (detail.id === 'edit-request') {
                setEditUserWorkSpaceProvisionRequestModalIsVisible(true);
              } else if (detail.id === 'approve-request') {
                handleApproval();
              } else if (detail.id === 'reject-request') {
                setRejectUserWorkSpaceProvisionRequestModalIsVisible(true);
              }
            }}
          >
            Actions
          </ButtonDropdown>
        </SpaceBetween>
      </>
    );
  };
  const renderTableHeader = () => {
    return (
      <Header
        counter={
          filteredUserWorkSpaceProvisionRequests.length
            ? `(${filteredUserWorkSpaceProvisionRequests.length}/${userWorkSpaceProvisionRequests.length})`
            : `(${userWorkSpaceProvisionRequests.length})`
        }
        actions={renderTableActions()}
      >
        WorkSpace Provision Requests
      </Header>
    );
  };

  const renderRejectUserWorkSpaceProvisionRequestModal = () => {
    return (
      <ChildModal
        onDismiss={() => {
          setRejectUserWorkSpaceProvisionRequestModalIsVisible(false);
        }}
        visible={rejectUserWorkSpaceProvisionRequestModalIsVisible}
        closeAriaLabel="Close modal"
        size="medium"
        header="Reject WorkSpace Provision Request"
      >
        <RejectUserWorkSpaceProvisionRequestForm
          userWorkSpaceProvisionRequests={
            selectedUserWorkSpaceProvisionRequests
          }
          setFlashBarItems={setFlashBarItems}
          setRejectUserWorkSpaceProvisionRequestModalIsVisible={
            setRejectUserWorkSpaceProvisionRequestModalIsVisible
          }
          refreshUserWorkSpaceProvisionRequestTable={
            refreshUserWorkSpaceProvisionRequestTable
          }
        />
      </ChildModal>
    );
  };

  const renderEditUserWorkSpaceProvisionRequestModal = () => {
    return (
      <ChildModal
        onDismiss={() => {
          setEditUserWorkSpaceProvisionRequestModalIsVisible(false);
        }}
        visible={editUserWorkSpaceProvisionRequestModalIsVisible}
        closeAriaLabel="Close modal"
        size="medium"
        header="Edit WorkSpace Provision Request"
      >
        <EditUserWorkSpaceProvisionRequestForm
          userWorkSpaceProvisionRequests={
            selectedUserWorkSpaceProvisionRequests
          }
          refreshUserWorkSpaceProvisionRequestTable={
            refreshUserWorkSpaceProvisionRequestTable
          }
        />
      </ChildModal>
    );
  };
  const renderUserWorkSpaceProvisionRequestDetailsModal = () => {
    return (
      <ChildModal
        onDismiss={() => {
          setUserWorkSpaceProvisionRequestDetailsModalIsVisible(false);
        }}
        visible={userWorkSpaceProvisionRequestDetailsModalIsVisible}
        closeAriaLabel="Close modal"
        size="large"
        header="WorkSpace Provision Request Details"
      >
        <UserWorkSpaceProvisionRequestDetails
          selectedUserWorkSpaceProvisionRequests={
            selectedUserWorkSpaceProvisionRequests
          }
        />
      </ChildModal>
    );
  };

  const tableColumnDefinitions = [
    {
      id: 'userName',
      header: 'Username',
      cell: (item: IUserWorkSpaceProvisionRequest) => item.userName || '-'
    },
    {
      id: 'corpEmail',
      header: 'Corp Email',
      cell: (item: IUserWorkSpaceProvisionRequest) => item.corpEmail || '-'
    },
    {
      id: 'bundleId',
      header: 'Bundle ID',
      cell: (item: IUserWorkSpaceProvisionRequest) => item.bundleId || '-'
    },
    {
      id: 'directoryId',
      header: 'Directory ID',
      cell: (item: IUserWorkSpaceProvisionRequest) => item.directoryId || '-'
    },
    {
      id: 'justification',
      header: 'Justification',
      cell: (item: IUserWorkSpaceProvisionRequest) => item.justification || '-'
    }
  ];

  return (
    <>
      <SpaceBetween direction="vertical" size="xs">
        <Flashbar items={flashBarItems} />
        <Table
          {...collectionProps}
          loading={tableIsLoading}
          trackBy="uuid"
          selectedItems={selectedUserWorkSpaceProvisionRequests}
          selectionType="single"
          columnDefinitions={tableColumnDefinitions}
          items={items}
          loadingText="Loading Users"
          onSelectionChange={({ detail }) =>
            handleTableRowSelection(detail.selectedItems)
          }
          header={renderTableHeader()}
          filter={
            <TextFilter
              {...filterProps}
              countText={`${filteredItemsCount} matches`}
              filteringAriaLabel="Filter Users"
            />
          }
          pagination={
            <Pagination
              {...paginationProps}
              ariaLabels={{
                nextPageLabel: 'Next page',
                previousPageLabel: 'Previous page',
                pageLabel: (pageNumber) => `Page ${pageNumber} of all pages`
              }}
            />
          }
        />
      </SpaceBetween>
      {renderRejectUserWorkSpaceProvisionRequestModal()}
      {renderEditUserWorkSpaceProvisionRequestModal()}
      {renderUserWorkSpaceProvisionRequestDetailsModal()}
    </>
  );
};
export default View;
